import React, { useRef, useEffect, useCallback } from 'react';
import { useAvatarUpload } from '../../lib/hooks/useAvatarUpload';
import { getAvatarUrl } from '../../lib/utils/fileUtils';
import { UPLOAD_CONFIG } from '../../lib/constants';
import { useI18n } from '../../contexts/I18nContext';
import './AvatarUpload.scss';

const AvatarUpload = ({ currentAvatar, onAvatarChange }) => {
  const { t } = useI18n();
  const fileInputRef = useRef(null);

  // Use custom Hook to manage upload logic
  const {
    isUploading,
    error,
    previewUrl,
    successMessage,
    handleFileSelect,
    cancelUpload,
    cleanup
  } = useAvatarUpload(onAvatarChange);

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Handle file input change
  const handleFileInputChange = useCallback((event) => {
    const file = event.target.files[0];
    handleFileSelect(file);
    // Clear input value to allow selecting the same file again
    event.target.value = '';
  }, [handleFileSelect]);

  // Click upload area
  const handleClick = useCallback(() => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  }, [isUploading]);

  // Get display avatar URL
  const getDisplayAvatar = useCallback(() => {
    // Prioritize local preview
    if (previewUrl) {
      return previewUrl;
    }
    // Otherwise display current avatar or default avatar
    return getAvatarUrl(currentAvatar);
  }, [previewUrl, currentAvatar]);

  return (
    <div className="avatar-upload">
      <div className="avatar-container">
        <div 
          className={`avatar-preview ${isUploading ? 'uploading' : ''}`} 
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          aria-label="Click to upload avatar"
        >
          <img 
            src={getDisplayAvatar()} 
            className="avatar-image"
            alt={t('personalCenter.myProfile.avatarSettings') || 'Avatar'}
            onError={(e) => {
              e.target.src = getAvatarUrl(null);
            }}
          />
          <div className="avatar-overlay">
            <div className="upload-icon">
              {isUploading ? (
                <div className="loading-spinner" aria-label="Uploading"></div>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 15V3M12 3L8 7M12 3L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L2 19C2 20.1046 2.89543 21 4 21L20 21C21.1046 21 22 20.1046 22 19L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <span className="upload-text">
              {isUploading ? t('personalCenter.myProfile.uploading') || 'Uploading...' : t('personalCenter.myProfile.clickToUpload') || 'Click to Upload'}
            </span>
            {isUploading && (
              <button 
                className="cancel-button"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelUpload();
                }}
                aria-label={t('personalCenter.myProfile.cancelUpload') || 'Cancel upload'}
              >
                {t('personalCenter.myProfile.cancel') || 'Cancel'}
              </button>
            )}
          </div>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept={UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.join(',')}
          onChange={handleFileInputChange}
          className="file-input"
          disabled={isUploading}
          aria-hidden="true"
        />
      </div>
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message" role="status" aria-live="polite">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="avatar-upload-success-icon">
            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="avatar-upload-success-text">{successMessage}</span>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="error-message" role="alert" aria-live="assertive">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="error-icon">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
            <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
          </svg>
          {error}
        </div>
      )}
      
      <div className="upload-tips">
        <p>{t('personalCenter.myProfile.supportedFormats') || `Supports ${UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(', ').replace(/\./g, '').toUpperCase()} formats`}</p>
        <p>{t('personalCenter.myProfile.fileSizeLimit') || `File size should not exceed ${UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB`}</p>
      </div>
    </div>
  );
};

export default AvatarUpload;
